import React from "react";

import { isValidKey } from "../..//utils/functional";
import { concatClassNames } from "../../utils/styles";
import { HeroLayoutProps, HeroLayoutSettings } from "./HeroLayout.interfaces";
import { checkForErrors } from "./hero-layout-error-checking";

import styles from "./HeroLayout.module.scss";

/** We work with a 12-column grid for layouts. */
export const COLUMNS = 12;

/**
 * See documentation on this in HeroLayoutSettings. Different layouts have
 * different needs in terms of whether content or media "drives" the layout,
 * and how media fills space available to it.
 */
const getAspectRatio = (
    mediaAspectRatio: HeroLayoutSettings["mediaAspectRatio"],
) => {
    if (mediaAspectRatio === "none" || mediaAspectRatio === "stretch")
        return null;

    const aspectRatioSplit = mediaAspectRatio.split(":");

    if (aspectRatioSplit.length > 1) {
        return (
            parseFloat(aspectRatioSplit[0]) / parseFloat(aspectRatioSplit[1])
        );
    } else {
        return null;
    }
};

/**
 * Probably the most complex and spaghetti-like part of the whole thing.
 * But at least it's contained?
 */
const getGridColumnPlacements = (settings: HeroLayoutSettings) => {
    const {
        heroDirection,
        heroPlacement,
        mediaPlacement,
        contentAlignPrimary,
        contentAlignSecondary,
        contentWidth,
        gapWidth = 0,
        mediaWidth,
    } = settings;

    let contentStart = 1;

    // Content is at the end of a row
    if (heroDirection === "row" && contentAlignSecondary === "right/bottom") {
        if (typeof mediaWidth !== "undefined") {
            contentStart = mediaWidth + gapWidth + 1;
        } else {
            contentStart = COLUMNS - contentWidth + gapWidth + 1;
        }
    }

    // Content is in a column but if it's not full width, center it
    // No half-columns, so Math.floor is being used.
    if (
        (heroDirection === "column" && contentAlignPrimary === "middle") ||
        (mediaPlacement !== "inline" && contentAlignSecondary === "middle")
    )
        contentStart = Math.floor((COLUMNS - contentWidth) / 2) + 1;

    // Content is in a column but if it's not full width, put the whole block
    // to the right
    if (heroDirection === "column" && contentAlignPrimary === "right/bottom")
        contentStart = COLUMNS - contentWidth + 1;

    const contentCols = contentWidth;

    let mediaStart = heroDirection === "row" ? contentWidth + 1 + gapWidth : 1;

    // Content is at the end of the row, so this needs to go first
    if (heroDirection === "row" && contentAlignSecondary === "right/bottom")
        mediaStart = 1;

    let mediaCols =
        mediaWidth ??
        (heroDirection === "row" ? COLUMNS - contentWidth : COLUMNS);

    if (
        (heroPlacement === "container" || heroPlacement === "full") &&
        mediaPlacement === "background"
    ) {
        mediaStart = 1;
        mediaCols = COLUMNS;
    }

    /**
     * Because we're using `number`s here, TS ends up inferring styles[whatever]
     * as `any`, which caused problems in the linting stage.
     *
     * Therefore, these get type guarded.
     */
    const contentStartKey = `gridStart${contentStart}`;
    const contentColKey = `gridCol${contentCols}`;
    const mediaStartKey = `gridStart${mediaStart}`;
    const mediaColKey = `gridCol${mediaCols}`;

    const contentStartClass = isValidKey(styles, contentStartKey)
        ? styles[contentStartKey]
        : "";
    const contentColsClass = isValidKey(styles, contentColKey)
        ? styles[contentColKey]
        : "";
    const mediaStartClass = isValidKey(styles, mediaStartKey)
        ? styles[mediaStartKey]
        : "";
    const mediaColsClass = isValidKey(styles, mediaColKey)
        ? styles[mediaColKey]
        : "";

    return {
        content: {
            start: contentStart,
            startClass: contentStartClass,
            colsClass: contentColsClass,
        },
        media: {
            start: mediaStart,
            startClass: mediaStartClass,
            colsClass: mediaColsClass,
        },
    };
};

export const HeroLayout = ({
    content,
    media,
    settings,
    styles: customStyles,
    errorChecking = true,
}: HeroLayoutProps) => {
    const {
        heroPlacement,
        heroDirection,
        textAlignment,
        contentAlignSecondary,
        contentAlignPrimary,
        mediaPlacement,
        mediaAspectRatio,
        minHeight,
        maxHeight,
    } = settings;

    const errors = errorChecking === true ? checkForErrors(settings) : null;
    if (errors) return errors;

    const aspectRatio = getAspectRatio(mediaAspectRatio);

    const gridPlacements = getGridColumnPlacements(settings);

    const flipOrder =
        (heroDirection === "row" &&
            gridPlacements.media.start < gridPlacements.content.start) ||
        (heroDirection === "column" &&
            contentAlignSecondary === "right/bottom");

    /**
     * The broad markup structure is root/wrapper > inner wrapper >
     * content and media.
     *
     * But there's an exception: when we want media to take the space of
     * root/wrapper while inner keeps its container + margin.
     */
    let placeMediaInsideInner = true;
    if (mediaPlacement === "background" && heroPlacement === "backgroundFull")
        placeMediaInsideInner = false;

    const contentClasses = concatClassNames([
        styles.content || "",
        styles[heroDirection] || "",
        gridPlacements.content.startClass,
        gridPlacements.content.colsClass,
        contentAlignPrimary === "left/top" ? styles.start : "",
        contentAlignPrimary === "middle" ? styles.center : "",
        contentAlignPrimary === "right/bottom" ? styles.end : "",
        contentAlignPrimary === "stretch" ? styles.stretch : "",
        styles[`${textAlignment}Text`] || "",
        // If media is in the background and it has an aspect ratio,
        // then it, not content, is driving the layout
        mediaPlacement === "background" &&
        aspectRatio !== null &&
        heroPlacement !== "backgroundFull"
            ? styles.absoluteLayout
            : "",
        mediaPlacement === "background" && content !== null
            ? styles.zIndex1
            : "",
        customStyles?.contentClassName || "",
    ]);

    const mediaClasses = concatClassNames([
        styles.media,
        gridPlacements.media.startClass,
        gridPlacements.media.colsClass,
        flipOrder ? styles.order0 : styles.order2,
        customStyles?.mediaClassName || "",
        // If media is in the background and it doesn't have an aspect ratio,
        // then it's not driving the layout, content is.
        mediaPlacement === "background" && aspectRatio === null
            ? styles.absoluteLayout
            : "",
        aspectRatio !== null ? styles.hasAspectRatio : "",
        // Typically this prop drives how a bit of content aligns with a bigger
        // image. But if the content is taller than the image, then the image
        // needs to align so that the content alignment looks correct
        contentAlignPrimary === "left/top" ? styles.selfStart : "",
        contentAlignPrimary === "middle" ? styles.selfCenter : "",
        contentAlignPrimary === "right/bottom" ? styles.selfEnd : "",
        contentAlignPrimary === "stretch" ? styles.selfStretch : "",
        mediaAspectRatio === "none" ? styles.dontFillMediaContainer : "",
        // Placing this below the contentAlignPrimary parts because I think
        // I want this to take precedence
        mediaAspectRatio === "stretch" ? styles.selfStretch : "",
    ]);

    const innerClasses = concatClassNames([
        styles[`${heroPlacement}Inner`],
        aspectRatio !== null && !placeMediaInsideInner
            ? styles.absoluteInner
            : "",
        styles[heroDirection],
        typeof maxHeight === "number" ? styles.overflowHidden : "",
        customStyles?.innerClassName,
    ]);

    const mediaElement =
        mediaPlacement !== "none" ? (
            <div
                className={mediaClasses}
                style={{
                    aspectRatio: aspectRatio || undefined,
                    maxHeight: maxHeight || undefined,
                }}
            >
                {media}
            </div>
        ) : (
            <></>
        );

    return (
        <div
            className={concatClassNames([
                styles.root,
                customStyles?.rootClassName,
            ])}
        >
            <div className={innerClasses} style={{ minHeight, maxHeight }}>
                {content !== null && (
                    <div className={contentClasses}>{content}</div>
                )}
                {placeMediaInsideInner && mediaElement}
            </div>

            {!placeMediaInsideInner && mediaElement}
        </div>
    );
};
